import { useEffect, useRef, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Dropdown,
    Input,
    QRCode,
    Radio,
    Rate,
    Space,
    Tag,
    message,
} from "antd";
import { CheckOutlined, CopyOutlined, DownloadOutlined, MessageOutlined, PictureOutlined, SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { createAppointmentCareLog, updateAppointmentCare, CareSurveyRatings } from "../../api/AppointmentCare";
import { usePermission } from "../../hooks/usePermission";
import { currentUser } from "../Social/socialStore";
import {
    EMPTY_EXTRA, SURVEY_CHANNEL, SURVEY_MAX_SENDS, SURVEY_REMIND_DAYS,
    careApi, surveyInviteText, surveySendsOf, type CareExtra, type SurveyChannel,
} from "./careStore";

/** Chép chữ vào clipboard, có đường lùi cho trang không chạy https */
async function copyText(text: string) {
    try {
        await navigator.clipboard.writeText(text);
    } catch {
        const el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
    }
}

const { TextArea } = Input;

const RATING_FIELDS: { key: keyof CareSurveyRatings; title: string }[] = [
    { key: "quality", title: "Chất lượng dịch vụ" },
    { key: "staff_attitude", title: "Thái độ nhân viên" },
    { key: "doctor_ktv", title: "Bác sĩ / KTV" },
    { key: "facilities", title: "Cơ sở vật chất" },
];

const EMPTY_RATINGS: CareSurveyRatings = { quality: 0, staff_attitude: 0, doctor_ktv: 0, facilities: 0 };

interface Props {
    appointmentId?: number;
    survey?: { care_note?: string; ratings?: Partial<CareSurveyRatings> } | null;
    isCared?: boolean;
    caredBy?: string | null;
    caredAt?: string | null;
    /** Dữ liệu chăm sóc lưu ở trình duyệt (lần gửi link, giới thiệu bạn bè) */
    extra?: CareExtra;
    customerName?: string;
    service?: string;
    phone?: string;
    onUpdated?: () => void;
    /** Ghi thêm vào hành trình xong thì báo để tab Hành trình tải lại */
    onLogged?: () => void;
}

export default function CsatTab({ appointmentId, survey, isCared, caredBy, caredAt, extra = EMPTY_EXTRA, customerName = "", service = "", phone = "", onUpdated, onLogged }: Props) {
    const { can } = usePermission("care");
    const [ratings, setRatings] = useState<CareSurveyRatings>(EMPTY_RATINGS);
    const [careNote, setCareNote] = useState("");
    const [saving, setSaving] = useState(false);
    // Đã chăm sóc thì mặc định xem phiếu (giao diện gốc), bấm "Chỉnh sửa" mới mở form
    const [editing, setEditing] = useState(false);
    const qrRef = useRef<HTMLDivElement>(null);

    // Link khảo sát công khai gửi cho khách (route /khao-sat/:id, không cần đăng nhập)
    const surveyUrl = appointmentId ? `${window.location.origin}/khao-sat/${appointmentId}` : "";
    const inviteText = surveyInviteText(customerName, service, surveyUrl);
    const sends = surveySendsOf(extra);
    const lastSend = sends[0];
    const remind = !isCared && !!lastSend && Date.now() - lastSend.at >= SURVEY_REMIND_DAYS * 86400000;
    const maxedOut = !isCared && sends.length >= SURVEY_MAX_SENDS;

    /** Ghi nhận đã gửi link: lưu lần gửi + ghi vào hành trình chăm sóc để cả nhóm thấy */
    const recordSend = async (channel: SurveyChannel) => {
        if (!appointmentId) return;
        const n = sends.length + 1;
        careApi.markSurveySent(appointmentId, channel, currentUser().name);
        const res = await createAppointmentCareLog(appointmentId, {
            type: 2,
            content: `Gửi link khảo sát cho khách tự đánh giá qua ${SURVEY_CHANNEL[channel]}${n > 1 ? ` (lần ${n})` : ""}`,
        });
        if (res.status) onLogged?.();
        message.success(`Đã ghi nhận gửi link lần ${n} qua ${SURVEY_CHANNEL[channel]}`);
    };

    const handleCopyLink = async () => {
        if (!surveyUrl) return;
        await copyText(surveyUrl);
        message.success("Đã sao chép link khảo sát");
    };

    const handleCopyInvite = async () => {
        await copyText(inviteText);
        message.success("Đã sao chép tin nhắn mời đánh giá");
    };

    // Zalo web không nhận nội dung soạn sẵn qua link → chép sẵn lời mời, mở khung chat để dán
    const handleSendZalo = async () => {
        if (!phone) { message.warning("Khách chưa có số điện thoại"); return; }
        await copyText(inviteText);
        window.open(`https://zalo.me/${phone.replace(/\D/g, "")}`, "_blank", "noopener");
        message.info("Đã chép lời mời — dán (Ctrl+V) vào khung chat Zalo rồi gửi", 4);
        await recordSend("zalo");
    };

    /**
     * Dựng ảnh QR để gửi cho khách.
     * Canvas của antd có nền TRONG SUỐT và không chừa lề trắng, gửi thẳng lên khung chat
     * nền tối là không quét được -> vẽ lại lên nền trắng, thêm lề và phóng to cho nét.
     */
    const buildQrCanvas = (): HTMLCanvasElement | null => {
        const source = qrRef.current?.querySelector("canvas");
        if (!source) return null;
        const size = 640;
        const padding = 40;
        const out = document.createElement("canvas");
        out.width = size + padding * 2;
        out.height = size + padding * 2;
        const ctx = out.getContext("2d");
        if (!ctx) return null;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.imageSmoothingEnabled = false; // giữ cạnh ô vuông sắc nét khi phóng to
        ctx.drawImage(source, padding, padding, size, size);
        return out;
    };

    const handleDownloadQR = () => {
        const canvas = buildQrCanvas();
        if (!canvas) {
            message.error("Chưa tạo được ảnh QR, vui lòng thử lại");
            return;
        }
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = `khao-sat-${appointmentId}.png`;
        a.click();
    };

    // Chép thẳng ảnh QR vào clipboard để dán luôn vào Zalo/Messenger, khỏi qua bước tải file
    const handleCopyQR = async () => {
        const canvas = buildQrCanvas();
        if (!canvas) {
            message.error("Chưa tạo được ảnh QR, vui lòng thử lại");
            return;
        }
        if (typeof window.ClipboardItem === "undefined" || !navigator.clipboard?.write) {
            message.warning("Trình duyệt không hỗ trợ chép ảnh, vui lòng dùng nút Tải ảnh QR");
            return;
        }
        try {
            // Truyền thẳng Promise blob vào ClipboardItem, KHÔNG await trước khi gọi write:
            // Chrome đòi write() nằm trong cùng nhịp tương tác của người dùng, await xong mới
            // gọi là mất "user activation" và bị chặn.
            const blobPromise: Promise<Blob> = new Promise((resolve, reject) => {
                canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("no blob"))), "image/png");
            });
            // Nếu cửa sổ trình duyệt không giữ focus, Chrome để write() treo vô hạn (không
            // resolve cũng không reject) -> chặn thời gian chờ để người dùng luôn có phản hồi.
            await Promise.race([
                navigator.clipboard.write([new window.ClipboardItem({ "image/png": blobPromise })]),
                new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 4000)),
            ]);
            message.success("Đã sao chép ảnh QR, dán vào khung chat để gửi khách");
        } catch {
            message.warning("Không chép được ảnh, vui lòng dùng nút Tải ảnh QR");
        }
    };

    useEffect(() => {
        setRatings({ ...EMPTY_RATINGS, ...(survey?.ratings || {}) } as CareSurveyRatings);
        setCareNote(survey?.care_note || "");
        setEditing(false);
    }, [appointmentId, survey]);

    const filled = RATING_FIELDS.map((f) => Number(ratings[f.key]) || 0).filter((v) => v > 0);
    const avg = filled.length ? Math.round((filled.reduce((s, v) => s + v, 0) / filled.length) * 10) / 10 : 0;
    // Lưu khảo sát lần đầu cần quyền create, sửa khảo sát đã lưu cần quyền edit
    const canSave = isCared ? can("edit") : can("create");
    const readOnly = (!!isCared && !editing) || !canSave;

    const handleSave = async () => {
        if (!appointmentId) return;
        const missingRating = RATING_FIELDS.some((f) => !ratings[f.key] || Number(ratings[f.key]) < 1);
        if (missingRating) {
            message.warning("Vui lòng đánh giá đủ 4 tiêu chí (1-5 sao)");
            return;
        }
        if (!careNote.trim()) {
            message.warning("Vui lòng nhập ghi chú chăm sóc");
            return;
        }
        setSaving(true);
        const res = await updateAppointmentCare(appointmentId, { care_note: careNote.trim(), ratings });
        setSaving(false);
        if (res.status) {
            message.success(res.msg || "Đã lưu khảo sát chăm sóc");
            setEditing(false);
            onUpdated?.();
        } else {
            message.error(res.msg || "Lưu khảo sát thất bại");
        }
    };

    return (

        <div className="p-5 bg-slate-50 dark:bg-[#1f1f1f]">

            {/* Link + QR để nhân viên gửi cho khách tự đánh giá — khách đánh giá xong thì lịch hẹn tự chuyển "Đã chăm sóc" */}
            {surveyUrl && (
                <Card className="mb-4">

                    <div className="flex items-start justify-between gap-2 mb-1">
                        <h2 className="font-semibold text-lg text-000000e0">
                            Gửi khách tự đánh giá
                        </h2>
                        {isCared ? (
                            <Tag color="success" className="rounded-full !mr-0">Khách đã đánh giá</Tag>
                        ) : lastSend ? (
                            <Tag color={remind ? "warning" : "purple"} className="rounded-full !mr-0">
                                {remind ? "Quá hạn chưa đánh giá" : "Chờ khách đánh giá"}
                            </Tag>
                        ) : (
                            <Tag className="rounded-full !mr-0">Chưa gửi link</Tag>
                        )}
                    </div>

                    <p className="text-[13px] text-slate-500 mb-4">
                        Gửi link hoặc ảnh QR bên dưới cho khách. Mỗi lịch hẹn chỉ đánh giá được một lần.
                        Gửi xong nhớ bấm <b>Đánh dấu đã gửi</b> để theo dõi khách nào chưa đánh giá.
                    </p>

                    {isCared ? null : maxedOut ? (
                        <Alert
                            className="mb-4"
                            type="warning"
                            showIcon
                            message={`Đã gửi link ${sends.length} lần mà khách chưa đánh giá — nên gọi khách khảo sát trực tiếp và điền phiếu bên dưới thay vì gửi tiếp.`}
                        />
                    ) : remind ? (
                        <Alert
                            className="mb-4"
                            type="warning"
                            showIcon
                            message={`Đã gửi link từ ${dayjs(lastSend.at).format("HH:mm DD/MM")} (${dayjs().diff(lastSend.at, "day")} ngày) mà khách chưa đánh giá — nhắc khách lần nữa.`}
                        />
                    ) : null}

                    <div className="flex flex-col sm:flex-row gap-4">

                        <div className="flex-1 min-w-0">

                            <Space.Compact className="w-full">
                                <Input readOnly value={surveyUrl} />
                                <Button icon={<CopyOutlined />} onClick={handleCopyLink}>
                                    Sao chép
                                </Button>
                            </Space.Compact>

                            <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 dark:bg-transparent p-3 text-[13px] text-slate-600 dark:text-slate-300">
                                {inviteText}
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                                {!isCared && (
                                    <Button type="primary" icon={<SendOutlined />} onClick={handleSendZalo} disabled={!phone || maxedOut}>
                                        {sends.length ? "Nhắc lại qua Zalo" : "Gửi qua Zalo"}
                                    </Button>
                                )}
                                <Button icon={<MessageOutlined />} onClick={handleCopyInvite}>
                                    Sao chép lời mời
                                </Button>
                                <Button icon={<PictureOutlined />} onClick={handleCopyQR}>
                                    Sao chép ảnh QR
                                </Button>
                                <Button icon={<DownloadOutlined />} onClick={handleDownloadQR}>
                                    Tải ảnh QR
                                </Button>
                                {!isCared && (
                                    <Dropdown
                                        trigger={["click"]}
                                        menu={{
                                            items: (["sms", "qr", "zalo", "other"] as SurveyChannel[]).map((c) => ({ key: c, label: SURVEY_CHANNEL[c] })),
                                            onClick: ({ key }) => recordSend(key as SurveyChannel),
                                        }}
                                    >
                                        <Button icon={<CheckOutlined />}>Đánh dấu đã gửi</Button>
                                    </Dropdown>
                                )}
                            </div>

                        </div>

                        <div ref={qrRef} className="shrink-0 self-center sm:self-start">
                            <QRCode value={surveyUrl} size={132} bordered={false} />
                        </div>

                    </div>

                    {sends.length > 0 && (
                        <div className="mt-4 border-t pt-3 text-[13px] text-slate-500 space-y-1">
                            <div className="font-medium text-000000e0">Lịch sử gửi link ({sends.length})</div>
                            {sends.map((x) => (
                                <div key={x.at}>
                                    {dayjs(x.at).format("HH:mm DD/MM/YYYY")} · {SURVEY_CHANNEL[x.channel]} · {x.by}
                                </div>
                            ))}
                        </div>
                    )}

                </Card>
            )}

            <Card>

                <div className="flex justify-between items-center mb-6 text-000000e0">

                    <h2 className="font-semibold text-lg">
                        Phiếu khảo sát hài lòng
                    </h2>

                    <Tag
                        color={avg === 0 ? "default" : avg < 3 ? "error" : avg < 4 ? "warning" : "success"}
                        className="rounded-full px-3"
                    >
                        {avg ? `${avg} / 5.0 Sao` : "Chưa đánh giá"}
                    </Tag>

                </div>

                {isCared && (caredBy || caredAt) && (
                    <div className="mb-4 rounded-lg bg-green-50 border border-green-200 px-4 py-2 text-[13px] text-green-700">
                        Đã chăm sóc{caredBy ? ` bởi ${caredBy}` : ""}{caredAt ? ` lúc ${dayjs(caredAt).format("HH:mm DD/MM/YYYY")}` : ""}
                    </div>
                )}

                <div className="divide-y text-000000e0">

                    {RATING_FIELDS.map((item) => (

                        <div
                            key={item.key}
                            className="flex justify-between py-5"
                        >

                            <span>
                                {item.title}
                            </span>

                            <Rate
                                disabled={readOnly}
                                value={Number(ratings[item.key]) || 0}
                                onChange={(v) => setRatings((prev) => ({ ...prev, [item.key]: v }))}
                            />

                        </div>

                    ))}

                </div>

                {/* Trường "giới thiệu bạn bè" backend chưa hỗ trợ — tạm lưu ở trình duyệt (careStore) */}
                <div className="mt-8 text-000000e0">

                    <div className="font-medium mb-3">
                        Khách có sẵn lòng giới thiệu bạn bè không?
                    </div>

                    <Radio.Group
                        disabled={!canSave}
                        value={extra.referral === true ? "yes" : extra.referral === false ? "no" : "unknown"}
                        onChange={(e) => appointmentId && careApi.setReferral(appointmentId, e.target.value === "yes" ? true : e.target.value === "no" ? false : null)}
                        optionType="button"
                        options={[
                            { label: "Có", value: "yes" },
                            { label: "Không", value: "no" },
                            { label: "Chưa hỏi", value: "unknown" },
                        ]}
                    />

                </div>

                <div className="mt-8 text-000000e0">

                    <div className="font-medium mb-3">
                        Ý kiến bổ sung của khách hàng:
                    </div>

                    {readOnly ? (
                        careNote ? (
                            <div
                                className={`rounded-xl border p-4 italic ${avg > 0 && avg < 3
                                    ? "border-red-200 bg-red-50 text-red-700"
                                    : "border-slate-200 bg-slate-50 text-slate-700"
                                    }`}
                            >
                                "{careNote}"
                            </div>
                        ) : (
                            <div className="text-slate-400">—</div>
                        )
                    ) : (
                        <TextArea
                            rows={4}
                            placeholder="Nhập nội dung ghi chú chăm sóc khách hàng..."
                            value={careNote}
                            onChange={(e) => setCareNote(e.target.value)}
                            maxLength={2000}
                            showCount
                        />
                    )}

                </div>

                <div className="mt-6 flex justify-end gap-3">
                    {readOnly ? (
                        canSave && (
                            <Button onClick={() => setEditing(true)}>
                                Chỉnh sửa khảo sát
                            </Button>
                        )
                    ) : (
                        <>
                            {isCared && (
                                <Button
                                    onClick={() => {
                                        setRatings({ ...EMPTY_RATINGS, ...(survey?.ratings || {}) } as CareSurveyRatings);
                                        setCareNote(survey?.care_note || "");
                                        setEditing(false);
                                    }}
                                >
                                    Hủy
                                </Button>
                            )}
                            <Button
                                type="primary"
                                loading={saving}
                                onClick={handleSave}
                            >
                                {isCared ? "Cập nhật khảo sát" : "Lưu khảo sát"}
                            </Button>
                        </>
                    )}
                </div>

            </Card>

        </div>

    );

}
